/**
 * One view model, three backend shapes.
 *
 * Prompt 24 asked for `/integrations/{id}/report`, `/api-keys/{id}/report` and
 * `/webhooks/{id}/report` to share a response shape so a single chart component
 * could render all three. What shipped does not:
 *
 *  - `ApiKeyReportResponse` carries `ApiKeyTotals` — `useCountLifetime` and
 *    `rateLimitPerMinute`, a count and a ceiling, not a quota. (Prompt 25 §8.10
 *    fixed this: it previously reused `ReportTotals` and put those two numbers in
 *    `quotaUsedToday`/`quotaLimit`, which is worse than different names because
 *    a shared component renders them as a quota and is silently wrong.)
 *  - `WebhookReportResponse` uses `attempted / succeeded / failed` and a
 *    five-component series point.
 *
 * So the adapting happens here, in one file, explicitly, rather than being
 * spread through three near-identical components or papered over by pointing one
 * component at all three. The three stay separate on purpose — an integration's
 * quota, a key's lifetime use and a webhook's delivery attempts are different
 * questions, and one shared shape would only be the old lie in a new place.
 */

import type {
  ApiKeyReportResponse, IntegrationReportResponse, WebhookReportResponse,
} from '@/shared/api/types'
import type { Point } from '@/shared/ui/charts'

export interface ReportModel {
  /** The KPI row. `null` means the backend does not report it for this subject. */
  tiles: { label: string; value: string; sub?: string; accent?: boolean }[]
  /** Small multiples: volume above, failures below, sharing an x domain. */
  volume: Point[]
  failures: Point[]
  latency: Point[]
  /** Ranked breakdowns, in render order. */
  breakdowns: { title: string; description?: string; points: Point[]; statusTone?: boolean }[]
  /** True when every number is zero — lets the caller explain silence. */
  empty: boolean
}

const nf = new Intl.NumberFormat('en-KE')
const num = (n: number | null | undefined) => (n == null ? '—' : nf.format(n))
const ms = (n: number | null | undefined) => (n == null ? '—' : `${nf.format(n)} ms`)
const pct = (n: number | null | undefined) =>
  n == null || Number.isNaN(n) ? '—' : `${(n * 100).toFixed(1)}%`

/**
 * A rate over no requests is undefined, not zero. The backend returns 0.0 for
 * an empty window, and rendering that as "0.0%" next to "nothing recorded"
 * reads as total failure rather than no data.
 */
const rate = (value: number, denominator: number) => (denominator === 0 ? '—' : pct(value))

/**
 * Bucket labels arrive as `YYYY-MM-DD` or `YYYY-MM-DDTHH`. Render them short —
 * the axis shows first/middle/last only, and the tooltip carries the full value.
 */
function bucketLabel(bucket: string): string {
  const [date, hour] = bucket.split('T')
  const parts = (date ?? '').split('-')
  const short = parts.length === 3 ? `${parts[2]}/${parts[1]}` : (date ?? bucket)
  return hour ? `${short} ${hour}:00` : short
}

/** Outcome codes are self-describing; this only decides the status dot. */
export function outcomeTone(outcome: string): 'good' | 'warn' | 'bad' | undefined {
  if (outcome === 'SUCCESS') return 'good'
  if (outcome === 'RATE_LIMITED' || outcome === 'CIRCUIT_OPEN' || outcome === 'NOT_FOUND') return 'warn'
  if (outcome === '__UNKNOWN__') return undefined
  return 'bad'
}

export function integrationReportModel(r: IntegrationReportResponse): ReportModel {
  const volume = r.series.map((s) => ({ label: bucketLabel(s.bucket), value: s.requests }))
  const failures = r.series.map((s) => ({ label: bucketLabel(s.bucket), value: s.errors }))
  const latency = r.series.map((s) => ({
    label: bucketLabel(s.bucket),
    value: s.p95LatencyMs ?? 0,
  }))

  const breakdowns: ReportModel['breakdowns'] = [
    {
      title: 'By capability',
      points: r.byCapability.map((c) => ({
        label: c.capability,
        value: c.requests,
        hint: c.errors ? `${num(c.errors)} failed` : undefined,
      })),
    },
    {
      title: 'By outcome',
      points: r.byOutcome.map((o) => ({ label: o.outcome, value: o.count })),
      statusTone: true,
    },
  ]

  // Provider-declared facets. The component knows nothing about gender or
  // county — whatever the provider declared is what appears.
  for (const [key, values] of Object.entries(r.dimensions ?? {})) {
    breakdowns.push({
      title: key,
      description: 'Provider-declared facet',
      points: values.map((v) => ({ label: v.dimensionValue, value: v.count })),
    })
  }

  return {
    tiles: [
      { label: 'Requests', value: num(r.totals.requests), accent: true },
      {
        label: 'Success rate',
        value: rate(r.totals.successRate, r.totals.requests),
        sub: `${num(r.totals.errors)} failed`,
      },
      { label: 'p95 latency', value: ms(r.totals.p95LatencyMs), sub: `p50 ${ms(r.totals.p50LatencyMs)}` },
      {
        label: 'Quota today',
        value: num(r.totals.quotaUsedToday),
        sub: r.totals.quotaLimit ? `of ${num(r.totals.quotaLimit)}` : undefined,
      },
    ],
    volume,
    failures,
    latency,
    breakdowns,
    empty: r.totals.requests === 0,
  }
}

export function apiKeyReportModel(r: ApiKeyReportResponse): ReportModel {
  return {
    tiles: [
      { label: 'Requests', value: num(r.totals.requests), accent: true },
      {
        label: 'Success rate',
        value: rate(r.totals.successRate, r.totals.requests),
        sub: `${num(r.totals.errors)} rejected`,
      },
      { label: 'p95 latency', value: ms(r.totals.p95LatencyMs), sub: `p50 ${ms(r.totals.p50LatencyMs)}` },
      // A count and a ceiling, labelled as such. Never "quota" — the two numbers
      // do not divide into one another and a percentage of them is meaningless.
      {
        label: 'Lifetime calls',
        value: num(r.totals.useCountLifetime),
        sub: r.totals.rateLimitPerMinute
          ? `${num(r.totals.rateLimitPerMinute)}/min limit`
          : undefined,
      },
    ],
    volume: r.series.map((s) => ({ label: bucketLabel(s.bucket), value: s.requests })),
    failures: r.series.map((s) => ({ label: bucketLabel(s.bucket), value: s.errors })),
    latency: r.series.map((s) => ({ label: bucketLabel(s.bucket), value: s.p95LatencyMs ?? 0 })),
    breakdowns: [
      {
        title: 'By route',
        description: 'Route templates, never resolved paths',
        points: r.byPath.map((p) => ({
          label: p.path,
          value: p.requests,
          hint: p.errors ? `${num(p.errors)} rejected` : undefined,
        })),
      },
      {
        title: 'By outcome',
        points: r.byOutcome.map((o) => ({ label: o.outcome, value: o.count })),
        statusTone: true,
      },
    ],
    empty: r.totals.requests === 0,
  }
}

export function webhookReportModel(r: WebhookReportResponse): ReportModel {
  const t = r.totals
  const delivered = t.attempted ? t.succeeded / t.attempted : Number.NaN
  return {
    tiles: [
      { label: 'Attempted', value: num(t.attempted), accent: true },
      { label: 'Delivered', value: t.attempted ? pct(delivered) : '—', sub: `${num(t.failed)} failed` },
      { label: 'p95 latency', value: ms(t.p95LatencyMs) },
      { label: 'Failed', value: num(t.failed) },
    ],
    volume: r.series.map((s) => ({ label: bucketLabel(s.bucket), value: s.attempted })),
    failures: r.series.map((s) => ({ label: bucketLabel(s.bucket), value: s.failed })),
    latency: r.series.map((s) => ({ label: bucketLabel(s.bucket), value: s.p95LatencyMs ?? 0 })),
    breakdowns: [
      {
        title: 'By status',
        points: r.byStatus.map((s) => ({ label: s.status, value: s.count })),
        statusTone: true,
      },
      {
        title: 'By event type',
        points: r.byEventType.map((e) => ({ label: e.eventType, value: e.count })),
      },
    ],
    empty: t.attempted === 0,
  }
}

/** Delivery statuses share the outcome tone vocabulary closely enough. */
export function deliveryTone(status: string): 'good' | 'warn' | 'bad' | undefined {
  if (status === 'DELIVERED') return 'good'
  if (status === 'PENDING') return 'warn'
  if (status === '__UNKNOWN__') return undefined
  return 'bad'
}
